<script setup lang="ts">
import { RelationshipToCustomer, useDependantAboutYouStore } from "../stores/dependantAboutYou";
import { Input, Question, DatePicker, RadioSet, Radio, Select } from "./fields";
import { tooltips } from "../tooltips/dependantAboutYouTooltips";
import { useYourDetailsStore, Title, MaritalStatus } from "../stores/yourDetails";
import Modal from "./Modal.vue";
import Medical from "./medicalConditionForms/Medical.vue";
import ValidationError from "./fields/ValidationError.vue";
import {
    MeasurementUnitType,
    SmokerStatus,
    MeasurementUnitLength,
    MeasurementUnitWeight,
} from "../stores/aboutYou";
import {
    yourDetailsValidator,
    aboutYouValidator,
    dependantAboutYouValidator,
    smokerValidator,
    dobMinDate,
    dobMaxDateDependant,
} from "../utils/validators";
import AlcoholCalculator from "./AlcoholCalculator.vue";
import {
    selectingHeightUnitWillClearTheOther,
    selectingWaistUnitWillClearTheOther,
    selectingWeightUnitWillClearTheOther,
} from "../composables/aboutYou";
import { MedicalCondition } from "@/entities/conditions";
import { gender } from "@/composables/aboutYou";
import { isNumber } from "@/utils";

const store = useDependantAboutYouStore();
const yourDetailsStore = useYourDetailsStore();

function hideModal() {
    store.toggleAlcoholModal = false;
}
const aboutValidator = aboutYouValidator(store);
const smokeValidator = smokerValidator(store);

const updateConditions = (conditions: MedicalCondition[]) => {
    store.selectedMedicalConditions = [...conditions];
};

</script>

<template data-test="dependantAboutYou">
    <div
        class="container disclaimer"
        data-test="disclaimer"
    >
        <div class="row">
            <div class="col-md-11">
                <p class="pb-3">
                    This section must be completed by the named spouse, registered civil partner or financial dependent partner (referred to as 'dependant').
                </p>
                <p class="pb-3">
                    This individual will receive the annuity payments if they live longer than the annuity policy holder.
                </p>
                <p>
                    By them completing this section and you continuing with the application you confirm that:
                </p>
                <ul>
                    <li>The information entered by the named person is accurate.</li>
                    <li>You understand quotes are based on the information provided by the named person as well as you.</li>
                </ul>
            </div>
        </div>
    </div>
    <Question
        name="title"
        label="Title"
    >
        <Select
            v-model.number="store.title"
            placeholder="Title"
            class="w-auto"
            :options="[
                {value: Title.Mr, label: 'Mr'},
                {value: Title.Miss, label: 'Miss'},
                {value: Title.Mrs, label: 'Mrs'},
                {value: Title.Ms, label: 'Ms'},
                {value: Title.Sir, label: 'Sir'},
                {value: Title.DrMale, label: 'Dr (Male)'},
                {value: Title.DrFemale, label: 'Dr (Female)'}]"
            :validation="yourDetailsValidator.title"
            @update:model-value="() => store.sex = gender(store.title)"
        />
    </Question>
    <Question
        name="fullName"
        label="Full name"
        type="group"
        :tooltip="tooltips.fullName"
    >
        <Input
            id="forename"
            v-model="store.forename"
            placeholder="Forename"
            hidden-label="Forename"
            type="text"
            :validation="yourDetailsValidator.forename"
        />
        <Input
            id="surname"
            v-model="store.surname"
            hidden-label="Surname"
            placeholder="Surname"
            type="text"
            :validation="yourDetailsValidator.surname"
        />
    </Question>
    <Question
        name="dob"
        type="group"
        label="Date of birth"
        input-class="col-12 col-md-11 col-lg-9"
        :tooltip="tooltips.dob"
    >
        <DatePicker
            v-model="store.dob"
            :min-date="dobMinDate"
            :max-date="dobMaxDateDependant"
            :validation="dependantAboutYouValidator.dob"
        />
    </Question>
    <Question
        name="postcode"
        label="Postcode"
        :tooltip="tooltips.postcode"
    >
        <Input
            v-model="store.postcode"
            placeholder="Postcode"
            input-class="w-auto"
            type="text"
            :validation="yourDetailsValidator.postcode"
        />
    </Question>
    <Question
        name="maritalStatus"
        label="Marital status"
    >
        <Select
            v-model.number="store.maritalStatus"
            placeholder="Please select"
            input-class="w-auto"
            :options="[
                {value: MaritalStatus.Married, label: 'Married'},
                {value: MaritalStatus.Divorced, label: 'Divorced'},
                {value: MaritalStatus.Separated, label: 'Separated'},
                {value: MaritalStatus.Single, label: 'Single'},
                {value: MaritalStatus.Widowed, label: 'Widowed'},
                {value: MaritalStatus.CivilPartnership, label: 'Civil partnership'},
                {value: MaritalStatus.DissolvedCivilPartnership, label: 'Dissolved civil partnership'},
                {value: MaritalStatus.Cohabiting, label: 'Cohabiting'},
                {value: MaritalStatus.SurvivingCivilPartnership, label: 'Surviving civil partnership'} ]"
            :validation="yourDetailsValidator.maritalStatus"
        />
    </Question>
    <Question
        name="relationshipToCustomer"
        label="Relationship to applicant"
    >
        <Select
            v-model.number="store.relationshipToCustomer"
            placeholder="Please select"
            input-class="w-auto"
            :options="[
                {value: RelationshipToCustomer.Dependant, label: 'Dependant'},
                {value: RelationshipToCustomer.Wife, label: 'Wife'},
                {value: RelationshipToCustomer.Husband, label: 'Husband'},
                {value: RelationshipToCustomer.CivilPartner, label: 'Civil Partner'},
                {value: RelationshipToCustomer.Cohabitant, label: 'Cohabitant'}]"
            :validation="dependantAboutYouValidator.relationshipToCustomer"
        />
    </Question>
    <h5 class="blue">
        Dependant's lifestyle
    </h5>
    <Question
        name="smoker"
        type="group"
        label="Are you or have you ever been a regular daily smoker?"
        :tooltip="tooltips.smoker"
    >
        <RadioSet>
            <Radio
                v-model="store.smoker"
                title="Currently"
                radio-type="responsive"
                :value="SmokerStatus.Currently"
                :validation="smokeValidator.smoker"
            />
            <Radio
                v-model="store.smoker"
                title="Previously"
                radio-type="responsive"
                :value="SmokerStatus.Previously"
                :validation="smokeValidator.smoker"
            />
            <Radio
                v-model="store.smoker"
                title="Never"
                radio-type="responsive"
                :value="SmokerStatus.Never"
                :validation="smokeValidator.smoker"
            />
        </RadioSet>
    </Question>
    <Question
        v-if="store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously"
        name="smokingStart"
        type="group"
        label="Approximately when did you start smoking?"
    >
        <DatePicker
            v-model="store.smokingStart"
            :max-date="new Date()"
            :min-date="new Date(yourDetailsStore.dob)"
            :show-day-field="false"
            :validation="smokeValidator.smokingStart"
        />
    </Question>
    <Question
        v-if="store.smoker === SmokerStatus.Previously"
        name="smokingEnd"
        type="group"
        label="Approximately when did you stop smoking?"
    >
        <DatePicker
            v-model="store.smokingEnd"
            :max-date="new Date()"
            :min-date="new Date(store.smokingEnd)"
            :show-day-field="false"
            :validation="smokeValidator.smokingEnd"
        />
    </Question>
    <div
        v-if="store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously"
        class="container disclaimer"
    >
        <div class="row">
            <div class="col-md-10">
                <p>Do/did you smoke:</p>
            </div>
        </div>
    </div>
    <template v-if="store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously">
        <Question
            name="cigarettes"
            type="group"
            label="Cigarettes"
        >
            <RadioSet>
                <Radio
                    v-model="store.cigarettes"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.cigarettes"
                />
                <Radio
                    v-model="store.cigarettes"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.cigarettes"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.cigarettes"
            name="cigarettesPerDay"
            label="How many cigarettes do/did you smoke each day?"
            :tooltip="tooltips.cigarettesPerDay"
        >
            <Input
                v-model.number="store.cigarettesPerDay"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.cigarettesPerDay"
            />
        </Question>
        <Question
            type="group"
            name="cigars"
            label="Cigars"
        >
            <RadioSet>
                <Radio
                    v-model="store.cigars"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.cigars"
                />
                <Radio
                    v-model="store.cigars"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.cigars"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.cigars"
            name="cigarsPerDay"
            label="How many cigars do/did you smoke each day?"
            :tooltip="tooltips.cigarsPerDay"
        >
            <Input
                v-model.number="store.cigarsPerDay"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.cigarsPerDay"
            />
        </Question>
        <Question
            type="group"
            name="rollingTobacco"
            label="Rolling tobacco"
        >
            <RadioSet>
                <Radio
                    v-model="store.rollingTobacco"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.rollingTobacco"
                />
                <Radio
                    v-model="store.rollingTobacco"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.rollingTobacco"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.rollingTobacco"
            name="rollingTobaccoPerWeek"
            label="How many grams of tobacco do/did you smoke each week?"
            :tooltip="tooltips.rollingTobaccoPerWeek"
        >
            <Input
                v-model.number="store.rollingTobaccoPerWeek"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.rollingTobaccoPerWeek"
            />
        </Question>
        <Question
            type="group"
            name="pipeTobacco"
            label="Pipe tobacco"
        >
            <RadioSet>
                <Radio
                    v-model="store.pipeTobacco"
                    title="Yes"
                    :value="true"
                    :validation="smokeValidator.pipeTobacco"
                />
                <Radio
                    v-model="store.pipeTobacco"
                    title="No"
                    :value="false"
                    :validation="smokeValidator.pipeTobacco"
                />
            </RadioSet>
        </Question>
        <Question
            v-if="store.pipeTobacco"
            name="pipeTobaccoPerWeek"
            label="How many grams of pipe tobacco do/did you smoke each week?"
            :tooltip="tooltips.pipeTobaccoPerWeek"
        >
            <Input
                v-model.number="store.pipeTobaccoPerWeek"
                placeholder="0"
                inputmode="numeric"
                type="number"
                :validation="smokeValidator.pipeTobaccoPerWeek"
            />
        </Question>
    </template>
    <div
        v-if="
            (store.smoker === SmokerStatus.Currently || store.smoker === SmokerStatus.Previously)
                && store.cigarettes === false
                && store.cigars === false
                && store.rollingTobacco === false
                && store.pipeTobacco === false
        "
        id="applicant_smoking_error"
        class="alert alert-danger text-center d-block"
    >
        You need to answer 'Yes' to at least one type of tobacco product.
    </div>
    <Question
        name="alcohol"
        label=""
        :tooltip="tooltips.alcohol"
    >
        <template #label>
            On average, how much alcohol do you drink in a week? Use our
            <a
                id="alcoholCalculator"
                href="#"
                @click.prevent="store.toggleAlcoholModal = true"
            >alcohol unit calculator</a>
        </template>

        <Input
            v-model.number="store.alcohol"
            placeholder="Units"
            class="col-md-12"
            input-class="input-width-auto"
            inputmode="numeric"
            type="number"
            :validation="aboutValidator.alcohol"
        />
    </Question>
    <hr>
    <h5 class="blue">
        Dependant's physique
    </h5>
    <Question
        type="group"
        name="yourHeight"
        label="How tall are you?"
        :tooltip="tooltips.height"
    >
        <Input
            v-if="store.heightUnit === MeasurementUnitType.Imperial"
            id="dependantHeightFeet"
            v-model.number="store.heightFeet"
            placeholder="Feet"
            hidden-label="Feet"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitLength.Feet"
            :filter="isNumber"
            :validation="aboutValidator.heightFeet"
        />
        <Input
            v-if="store.heightUnit === MeasurementUnitType.Imperial"
            id="dependantHeightInches"
            v-model.number="store.heightInches"
            placeholder="Inches"
            hidden-label="Inches"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitLength.Inches"
            :filter="isNumber"
            :validation="aboutValidator.heightInches"
        />

        <Input
            v-if="store.heightUnit === MeasurementUnitType.Metric"
            v-model.number="store.heightCentimetres"
            placeholder="Centimetres"
            inputmode="numeric"
            hidden-label="Centimetres"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitLength.Centimetres"
            :filter="isNumber"
            :validation="aboutValidator.heightCentimetres"
        />

        <RadioSet col="col-md-4">
            <Radio
                v-model="store.heightUnit"
                child-class="pill-start"
                radio-type="pill"
                title="Imperial"
                :value="MeasurementUnitType.Imperial"
                :validation="aboutValidator.heightUnit"
                @click="selectingHeightUnitWillClearTheOther(store)"
            />
            <Radio
                v-model="store.heightUnit"
                child-class="pill-end"
                radio-type="pill"
                title="Metric"
                :value="MeasurementUnitType.Metric"
                :validation="aboutValidator.heightUnit"
                @click="selectingHeightUnitWillClearTheOther(store)"
            />
        </RadioSet>
    </Question>
    <Question
        type="group"
        name="yourWeight"
        label="What do you weigh?"
        :tooltip="tooltips.weight"
    >
        <Input
            v-if="store.weightUnit === MeasurementUnitType.Imperial"
            id="dependantWeightStone"
            v-model.number="store.weightStone"
            placeholder="Stone"
            hidden-label="Stone"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitWeight.Stone"
            :filter="isNumber"
            :validation="aboutValidator.weightStone"
        />
        <Input
            v-if="store.weightUnit === MeasurementUnitType.Imperial"
            id="dependantWeightPounds"
            v-model.number="store.weightPounds"
            placeholder="Pounds"
            hidden-label="Pounds"
            inputmode="numeric"
            type="number"
            class="col-md-3"
            :append="MeasurementUnitWeight.Pounds"
            :filter="isNumber"
            :validation="aboutValidator.weightPounds"
        />

        <Input
            v-if="store.weightUnit === MeasurementUnitType.Metric"
            id="dependantWeightKg"
            v-model.number="store.weightKg"
            placeholder="Kilogram"
            hidden-label="Kilogram"
            inputmode="numeric"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitWeight.Kilogram"
            :filter="isNumber"
            :validation="aboutValidator.weightKg"
        />

        <RadioSet
            col="col-md-4"
        >
            <Radio
                v-model="store.weightUnit"
                child-class="pill-start"
                radio-type="pill"
                title="Imperial"
                :value="MeasurementUnitType.Imperial"
                :validation="aboutValidator.weightUnit"
                @click="selectingWeightUnitWillClearTheOther(store)"
            />
            <Radio
                v-model="store.weightUnit"
                child-class="pill-end"
                radio-type="pill"
                title="Metric"
                :value="MeasurementUnitType.Metric"
                :validation="aboutValidator.weightUnit"
                @click="selectingWeightUnitWillClearTheOther(store)"
            />
        </RadioSet>
    </Question>
    <Question
        name="yourWaist"
        type="group"
        label="What is your waist measurement? (optional)"
        :tooltip="tooltips.waist"
    >
        <Input
            v-if="store.waistUnit === MeasurementUnitType.Imperial"
            id="dependantWaistInches"
            v-model.number="store.waistInches"
            placeholder="Inches"
            hidden-label="Inches"
            inputmode="numeric"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitLength.Inches"
            :filter="isNumber"
            :validation="aboutValidator.waistInches"
        />

        <Input
            v-if="store.waistUnit === MeasurementUnitType.Metric"
            v-model.number="store.waistCentimetres"
            placeholder="Centimetres"
            hidden-label="Centimetres"
            inputmode="numeric"
            type="number"
            class="col-md-6"
            :append="MeasurementUnitLength.Centimetres"
            :filter="isNumber"
            :validation="aboutValidator.waistCentimetres"
        />

        <RadioSet
            col="col-md-4"
        >
            <Radio
                v-model="store.waistUnit"
                child-class="pill-start"
                radio-type="pill"
                title="Imperial"
                :value="MeasurementUnitType.Imperial"
                @click="selectingWaistUnitWillClearTheOther(store)"
            />
            <Radio
                v-model="store.waistUnit"
                child-class="pill-end"
                radio-type="pill"
                title="Metric"
                :value="MeasurementUnitType.Metric"
                @click="selectingWaistUnitWillClearTheOther(store)"
            />
        </RadioSet>
    </Question>
    <hr>
    <h5 class="blue">
        Dependant's health
    </h5>
    <div class="container disclaimer">
        <div class="row">
            <div class="col-md-10">
                <p>Please be completely honest about any conditions you have or have had in the past. If you are unsure, please declare the condition. The provider will assess your declared medical condition(s) later to decide if the income quoted is correct.</p><br>
                <p>There is no limit to how far back you should go in your history. For example, if you had cancer as a child, you should declare this even if you have had no recurrence since then.</p><br>
                <p>Remember, your medical conditions may impact the annuity income offered by the provider. This is because the providers consider the life expectancy for both lives.</p><br>
            </div>
        </div>
    </div>
    <Question
        name="medical"
        type="group"
        label="Do you have, or have you ever had any medical conditions?"
        :tooltip="tooltips.medical"
    >
        <RadioSet>
            <Radio
                id="dependant_medicalconditions"
                v-model="store.medical"
                title="Yes"
                :value="true"
                :validation="aboutValidator.medical"
            />
            <Radio
                id="dependant_medicalconditions_false"
                v-model="store.medical"
                title="No"
                :value="false"
                :validation="aboutValidator.medical"
            />
        </RadioSet>
    </Question>
    <Modal
        id="dependant-alcohol-calculator"
        open-focus="close-dependant-alcohol-calculator"
        close-focus="alcoholCalculator"
        header="Alcohol calculator"
        :show="store.toggleAlcoholModal"
        @close="hideModal"
    >
        <template #body>
            <AlcoholCalculator
                v-model="store.alcohol"
                @close="hideModal"
            />
        </template>
    </Modal>
    <Medical
        v-if="store.medical"
        :person-details="store.getPersonDataForMedical"
        :medical-conditions="store.selectedMedicalConditions"
        @update:conditions="updateConditions"
    />
    <ValidationError
        v-if="store.medical && store.selectedMedicalConditions.length === 0"
        :validation="aboutValidator.medicalQuantity"
        class="alert alert-danger text-center"
        :values="{}"
        name="no declared conditions"
    />
</template>

<style lang="scss">
.disclaimer {
    background-color: #ffffff;
    padding: 20px !important;
    margin-bottom: 20px;
}
</style>
